<template>
    <Teleport to="body">
        <Transition
            enter-active-class="transition ease-out duration-300"
            enter-from-class="opacity-0 translate-y-2"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition ease-in duration-200"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 translate-y-2"
        >
            <div
                v-if="show"
                class="fixed top-4 right-4 max-w-md w-full shadow-lg rounded-lg pointer-events-auto overflow-hidden"
                :class="[typeClasses]"
                style="z-index: 99999;"
            >
                <div class="p-4">
                    <div class="flex items-start">
                        <div class="flex-shrink-0">
                            <!-- Success Icon -->
                            <svg v-if="type === 'success'" class="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                            </svg>

                            <!-- Error Icon -->
                            <svg v-else-if="type === 'error'" class="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                            </svg>

                            <!-- Warning Icon -->
                            <svg v-else-if="type === 'warning'" class="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                            </svg>

                            <!-- Info Icon -->
                            <svg v-else class="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                            </svg>
                        </div>

                        <div class="ml-3 w-0 flex-1 pt-0.5">
                            <p class="text-sm font-medium">
                                {{ message }}
                            </p>
                        </div>

                        <div class="ml-4 flex-shrink-0 flex">
                            <button
                                @click="close"
                                class="rounded-md inline-flex focus:outline-none focus:ring-2 focus:ring-offset-2"
                                :class="closeButtonClasses"
                            >
                                <span class="sr-only">Close</span>
                                <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
    type: {
        type: String,
        default: 'info',
        validator: (value) => ['success', 'error', 'warning', 'info'].includes(value)
    },
    message: {
        type: String,
        required: true
    },
    duration: {
        type: Number,
        default: 4000
    },
    show: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['close']);

const typeClasses = computed(() => {
    const classes = {
        success: 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-200',
        error: 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200',
        warning: 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200',
        info: 'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200'
    };
    return classes[props.type] || classes.info;
});

const closeButtonClasses = computed(() => {
    const classes = {
        success: 'text-green-400 hover:text-green-500 focus:ring-green-500',
        error: 'text-red-400 hover:text-red-500 focus:ring-red-500',
        warning: 'text-yellow-400 hover:text-yellow-500 focus:ring-yellow-500',
        info: 'text-blue-400 hover:text-blue-500 focus:ring-blue-500'
    };
    return classes[props.type] || classes.info;
});

let timeout = null;

watch(() => props.show, (newVal) => {
    if (newVal && props.duration > 0) {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            close();
        }, props.duration);
    }
});

const close = () => {
    clearTimeout(timeout);
    emit('close');
};
</script>
